import { isGoalRaffle } from "react-ui-components/components/raffle/raffleGoalUtils"

export const getBatchName = (ticket: Record<string, any> | null | undefined) => {
  if (!ticket) {
    return "Not named"
  }

  const candidateKeys = ["name", "batchName", "description", "ticketName", "title", "label"]

  for (const key of candidateKeys) {
    const candidateValue = ticket[key]
    if (typeof candidateValue === "string" && candidateValue.trim()) {
      return candidateValue.trim()
    }
  }

  return "Not named"
}

export const getQrToken = (ticket: Record<string, any> | null | undefined) => {
  if (typeof ticket?.sundryShortUrl === "string" && ticket.sundryShortUrl.trim()) {
    return ticket.sundryShortUrl
  }

  if (!ticket || typeof ticket.qrCodeData !== "string") {
    return ""
  }

  return ticket.qrCodeData
}

export const getRawQrToken = (ticket: Record<string, any> | null | undefined) => {
  if (typeof ticket?.rawQrCodeData === "string" && ticket.rawQrCodeData.trim()) {
    return ticket.rawQrCodeData
  }

  return getQrToken(ticket)
}

export const getCodePrefix = (ticket: Record<string, any> | null | undefined) => {
  if (typeof ticket?.codePrefix !== "string") {
    return ""
  }

  return ticket.codePrefix.trim()
}

export const getTicketTypes = (ticket: Record<string, any> | null | undefined) => {
  if (!Array.isArray(ticket?.ticketTypes)) {
    return []
  }

  return ticket.ticketTypes.filter(
    (type: unknown): type is string => typeof type === "string" && !!type.trim(),
  )
}

const normalizeTicketTypeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "")

export const hasRafflePrepayTicket = (ticket: Record<string, any> | null | undefined) => {
  const storedTicketTypes = getTicketTypes(ticket)
  if (storedTicketTypes.some((ticketType) => normalizeTicketTypeKey(ticketType) === "raffleprepay")) {
    return true
  }

  if (!Array.isArray(ticket?.ticketTypeKeys)) {
    return false
  }

  return ticket.ticketTypeKeys.some(
    (ticketTypeKey: unknown) =>
      typeof ticketTypeKey === "string" && normalizeTicketTypeKey(ticketTypeKey) === "raffleprepay",
  )
}

export const getRaffleTicketsCount = (ticket: Record<string, any> | null | undefined) => {
  if (typeof ticket?.raffleTicketsCount === "number" && Number.isFinite(ticket.raffleTicketsCount)) {
    return ticket.raffleTicketsCount
  }

  return hasRafflePrepayTicket(ticket) ? 1 : null
}

export const getScanCooldownMinutes = (ticket: Record<string, any> | null | undefined) => {
  if (typeof ticket?.scanCooldownMinutes === "number" && Number.isFinite(ticket.scanCooldownMinutes)) {
    return ticket.scanCooldownMinutes
  }

  return 60
}

const toOptionalNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value === "string" && value.trim()) {
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) ? parsedValue : undefined
  }

  return undefined
}

const toOptionalPositiveInteger = (value: unknown) => {
  const parsedValue = toOptionalNumber(value)
  return typeof parsedValue === "number" && Number.isInteger(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined
}

const toOptionalNonNegativeInteger = (value: unknown) => {
  const parsedValue = toOptionalNumber(value)
  return typeof parsedValue === "number" && Number.isInteger(parsedValue) && parsedValue >= 0
    ? parsedValue
    : undefined
}

export const getTicketValue = (ticket: Record<string, any> | null | undefined) => {
  const parsedValue = toOptionalNumber(ticket?.ticketsPointsValue ?? ticket?.value)
  return typeof parsedValue === "number" ? parsedValue : undefined
}

export const getTicketPoints = (ticket: Record<string, any> | null | undefined) => {
  const parsedValue = toOptionalNumber(ticket?.ticketsPoints ?? ticket?.points)
  return typeof parsedValue === "number" ? parsedValue : undefined
}

export const isUnlimitedTicketBatch = (ticket: Record<string, any> | null | undefined) =>
  Boolean(
    ticket?.unlimitedTotalScans === true ||
      ticket?.unlimitedScans === true ||
      ticket?.availableCount === -1 ||
      ticket?.availableCount === "unlimited",
  )

const hasStringValue = (value: unknown) => typeof value === "string" && value.trim().length > 0

const hasStringArrayValue = (value: unknown) =>
  Array.isArray(value) && value.some((item) => hasStringValue(item))

export type NormalizedTicketBatchRow = Record<string, any> & {
  id?: string
  name?: string
  points?: number
  ticketsPoints?: number
  value?: number
  ticketsPointsValue?: number
}

export const isGeneratedScanBatchTicket = (ticket: Record<string, any> | null | undefined) => {
  if (!ticket) {
    return false
  }

  return Boolean(
    hasStringValue(ticket.qrCodeData) ||
      hasStringValue(ticket.rawQrCodeData) ||
      hasStringValue(ticket.sundryQrId) ||
      hasStringValue(ticket.sundryShortCode) ||
      hasStringValue(ticket.sundryShortUrl) ||
      hasStringValue(ticket.codePrefix) ||
      hasStringArrayValue(ticket.ticketTypeKeys) ||
      hasStringArrayValue(ticket.ticketTypes),
  )
}

export const normalizeTicketBatchRow = (ticket: Record<string, any>): NormalizedTicketBatchRow => {
  const batchName = getBatchName(ticket)
  const value = getTicketValue(ticket)
  const points = getTicketPoints(ticket)
  const unlimitedTotalScans = isUnlimitedTicketBatch(ticket)

  return {
    ...ticket,
    ...(batchName === "Not named" ? {} : { name: batchName }),
    ...(typeof value === "number" ? { ticketsPointsValue: value, value } : {}),
    ...(typeof points === "number" ? { ticketsPoints: points, points } : {}),
    ...(unlimitedTotalScans ? { availableCount: -1, unlimitedTotalScans: true } : {}),
  }
}

// Mirrors `isActiveAdminUserTicket` in `Users/UserTickets.tsx` (same disabled/expirationDate
// shape) so a batch's own expiration is treated consistently across admin ticket views.
export const isActiveTicketBatchRow = (
  ticket: Record<string, any> | null | undefined,
  now = Date.now(),
) =>
  Boolean(
    ticket &&
      !ticket.disabled &&
      (typeof ticket.expirationDate !== "number" || ticket.expirationDate >= now),
  )

export type RaffleExpiryFields = {
  disabled?: boolean
  raffleDate?: string | number | Date
  raffleMode?: string
  type?: string
  goalSettings?: any
}

// A batch's own `expirationDate` (checked above) is independent of the parent raffle's own end
// (drawn, or disabled by an admin) - selling/generating a batch early doesn't retroactively set
// an expiration on it. Mirrors win-facil-web-app's `hasRaffleExpired`
// (src/contexts/raffleVisibility.ts) without importing across apps: a raffle "ends" once
// disabled, or (non-goal raffles, which have no drawing date) once `raffleDate` has passed.
// `raffleDate` is coerced through `new Date(...)` rather than compared as a strict `number`,
// since `IRaffle.raffleDate` is typed `string | number | Date` - a strict-number check silently
// treated a raffle as never-expired whenever it was stored as a Timestamp/ISO string.
export const hasRaffleExpired = (
  raffle: RaffleExpiryFields | null | undefined,
  now = Date.now(),
) => {
  if (!raffle) {
    return false
  }

  if (raffle.disabled) {
    return true
  }

  if (isGoalRaffle(raffle as any)) {
    return false
  }

  if (!raffle.raffleDate) {
    return false
  }

  return new Date(raffle.raffleDate).getTime() < now
}

export const normalizeTicketBatchList = (
  tickets: Record<string, any>[],
  includeExpired = false,
  raffle?: RaffleExpiryFields | null,
) => {
  const raffleExpired = hasRaffleExpired(raffle)

  return tickets
    .map(normalizeTicketBatchRow)
    .filter(isGeneratedScanBatchTicket)
    .filter(
      (ticket) => includeExpired || (isActiveTicketBatchRow(ticket) && !raffleExpired),
    ) as NormalizedTicketBatchRow[]
}

const buildScanCode = (rawQrCode: string, codePrefix: string) =>
  codePrefix ? `${codePrefix}${rawQrCode}` : rawQrCode

export const formatTicketPayload = (ticket: Record<string, any>): Record<string, any> => {
  const codePrefix = getCodePrefix(ticket)
  const rawQrCodeData = getRawQrToken(ticket).trim()
  const availableCount = toOptionalNumber(ticket.availableCount)
  const value = getTicketValue(ticket)
  const points = getTicketPoints(ticket)
  const userScanCount = toOptionalNonNegativeInteger(ticket.userScanCount)
  const scanCooldownMinutes = toOptionalNonNegativeInteger(ticket.scanCooldownMinutes)
  const raffleTicketsCount = toOptionalPositiveInteger(ticket.raffleTicketsCount)
  const unlimitedTotalScans = isUnlimitedTicketBatch(ticket)

  return {
    ...ticket,
    ...(unlimitedTotalScans
      ? { availableCount: -1, unlimitedTotalScans: true }
      : typeof availableCount === "number"
      ? { availableCount }
      : {}),
    ...(typeof value === "number" ? { value } : {}),
    ...(typeof points === "number" ? { points } : {}),
    ...(typeof value === "number" ? { ticketsPointsValue: value } : {}),
    ...(typeof points === "number" ? { ticketsPoints: points } : {}),
    ...(typeof userScanCount === "number" ? { userScanCount } : {}),
    ...(typeof scanCooldownMinutes === "number" ? { scanCooldownMinutes } : {}),
    ...(typeof raffleTicketsCount === "number" ? { raffleTicketsCount } : {}),
    ...(codePrefix ? { codePrefix } : {}),
    ...(rawQrCodeData ? { rawQrCodeData } : {}),
    ...(rawQrCodeData
      ? { qrCodeData: ticket.sundryShortUrl || buildScanCode(rawQrCodeData, codePrefix) }
      : {}),
  }
}
